import { Timestamp } from 'firebase/firestore';
export interface SchoolYear {
  startDate: Timestamp; // August 1st
  isCustomDate: boolean;
  confirmedNoRights?: boolean; // Only for custom dates
  year: number;
}

/**
 * Configuration constants for school year calculations
 * These are used by standalone functions that can't use dependency injection
 *
 * ⚠️ These constants are kept in sync with the config service defaults.
 * To change these values application-wide, use the admin config UI or
 * inject SchoolyearService which uses the centralized ConfigService.
 *
 * MIN_SCHOOL_YEAR = 2026: The app launches with school year 2026/27 only.
 * No 2025/26 data exists, so clamping getCurrentSchoolYear() to 2026 is
 * intentional (Issue #301). Before Aug 2027 the clamp is a no-op anyway,
 * because the natural calculation will return >= 2026 from Aug 2026 onward.
 */
export const MIN_SCHOOL_YEAR = 2026;
export const UPCOMING_YEARS_COUNT = 4;

/**
 * Get the current school year based on the current date
 *
 * Note: This function uses hardcoded constants. For reactive config values,
 * inject SchoolyearService from @awo-care/shared-core instead.
 */
export function getCurrentSchoolYear(): SchoolYear {
  const now = new Date();
  const year = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  const minYear = MIN_SCHOOL_YEAR;

  return {
    startDate: Timestamp.fromDate(
      new Date(Date.UTC(year >= minYear ? year : minYear, 7, 1))
    ),
    isCustomDate: false,
    year: year >= minYear ? year : minYear,
  };
}

/**
 * Get upcoming school years for dropdown selection
 *
 * Note: This function uses hardcoded constants. For reactive config values,
 * inject SchoolyearService from @awo-care/shared-core instead.
 */
export function getUpcomingSchoolYears(): SchoolYear[] {
  const currentSchoolYear = getCurrentSchoolYear();

  return Array.from({ length: UPCOMING_YEARS_COUNT }, (_, i) => currentSchoolYear.year + i)
    .map((year) => ({
      startDate: Timestamp.fromDate(new Date(Date.UTC(year, 7, 1))), // August 1st UTC
      isCustomDate: false,
      year,
    }));
}

/**
 * Schuljahre, die im Anmeldeformular zur Auswahl stehen: das Vorjahr (fuer
 * Nachzuegler), das laufende und die kommenden Jahre. Bewusst unabhaengig von
 * den Preisschienen der Schulen — Eltern melden ihre Kinder teils 1–2 Jahre im
 * Voraus an, lange bevor AWO eine Schiene fuer das Jahr angelegt hat. Die
 * Preise fuer ein Jahr ohne eigene Schiene loest resolvePricing ueber die
 * aktive Schiene auf.
 *
 * Das Vorjahr entfaellt, wenn es vor MIN_SCHOOL_YEAR liegt — davor existieren
 * keine Daten (Issue #301).
 */
export function getRegistrationSchoolYears(
  currentYear: number,
  upcomingCount: number = UPCOMING_YEARS_COUNT,
  minYear: number = MIN_SCHOOL_YEAR
): number[] {
  const firstYear = Math.max(currentYear - 1, minYear);
  const lastYear = currentYear + upcomingCount - 1;
  const years: number[] = [];
  for (let year = firstYear; year <= lastYear; year++) {
    years.push(year);
  }
  return years;
}

/**
 * Individueller Starttermin. `year` ist das im Formular gewählte Schuljahr,
 * nicht das Kalenderjahr des Termins: Module und Preise kommen aus der Schiene
 * des gewählten Jahres, und der Server prüft das Modul gegen `yearInfo.year`.
 * Mit `date.getFullYear()` scheiterte z. B. 2026/27 mit Start 01.02.2027.
 */
export function createCustomSchoolYear(
  date: Date,
  confirmedNoRights: boolean,
  schoolYear: number
): SchoolYear {
  return {
    startDate: Timestamp.fromDate(date),
    isCustomDate: true,
    confirmedNoRights,
    year: schoolYear,
  };
}

/**
 * Wählbarer Zeitraum für einen individuellen Starttermin: ab heute, frühestens
 * 01.08. des Schuljahres, spätestens 31.07. des Folgejahres (lokale Tage, wie
 * der Datepicker sie liefert).
 */
export function customStartDateBounds(
  schoolYear: number,
  now: Date = new Date()
): { min: Date; max: Date } {
  const yearStart = new Date(schoolYear, 7, 1);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return {
    min: today > yearStart ? today : yearStart,
    max: new Date(schoolYear + 1, 6, 31),
  };
}

export function isCustomStartDateInSchoolYear(
  date: Date,
  schoolYear: number,
  now: Date = new Date()
): boolean {
  const { min, max } = customStartDateBounds(schoolYear, now);
  return date >= min && date <= max;
}
